import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

function formatDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"];
  const date = new Date(y, m - 1, d);
  return `${days[date.getDay()]} ${months[m - 1]} ${d}`;
}

function formatArrivalTime(isoDateTime) {
  // Extract time from "2024-04-15T21:00:00-06:00" into "9:00 PM" format
  if (!isoDateTime) return "";

  // Handle both datetime and date-only formats
  const match = isoDateTime.match(/T(\d{2}):(\d{2})/);
  if (!match) {
    // If no time found but it's a valid date, time wasn't set, so return empty
    return "";
  }

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = hours >= 12 ? "PM" : "AM";
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${meridiem}`;
}

function extractMinutesFromDateTime(isoDateTime) {
  // Extract time from "2024-04-15T21:00:00-06:00" into minutes since midnight
  if (!isoDateTime) return null;
  const match = isoDateTime.match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  return hours * 60 + minutes;
}

function renderActivityText(label) {
  const text = (label || "").toString();
  if (!text.includes("/")) return text;
  const parts = text.split("/");
  return parts.map((part, idx) => (
    <React.Fragment key={`${part}-${idx}`}>
      {idx > 0 ? "/\u200b" : ""}
      {part.trimStart()}
    </React.Fragment>
  ));
}

function DayCard({ day, arrivalTime, isArrivalDay }) {
  const isHorseback = (label) => (label || "").toLowerCase().includes("horseback riding");
  const formatActivity = (label, level) =>
    isHorseback(label) && level ? `${label} - ${level}` : label;
  return (
    <div
      className="guest-day-card"
      style={{
        border: "1px solid #c6ced0",
        borderRadius: 12,
        padding: 12,
        background: "rgba(255, 255, 255, 0.92)",
        boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ fontWeight: 800 }}>{formatDateLabel(day.date)}</div>
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontWeight: 700, minWidth: 60, whiteSpace: "nowrap" }}>
            {isArrivalDay ? (formatArrivalTime(arrivalTime) || "Arrival") : (day.am?.startTime || "AM")}
          </div>
          <div>
            {isArrivalDay && day.am === null ? (
              "Arrival"
            ) : isArrivalDay && day.am ? (
              <>
                <span className="activity-text">
                  {renderActivityText(formatActivity(day.am.activity, day.level))}
                </span>
                {day.am.activityUrls?.length ? (
                  <span style={{ marginLeft: 8 }}>
                    <a href={day.am.activityUrls[0]} target="_blank" rel="noreferrer">
                      View activity
                    </a>
                  </span>
                ) : null}
              </>
            ) : day.am ? (
              <>
                <span className="activity-text">
                  {renderActivityText(formatActivity(day.am.activity, day.level))}
                </span>
                {day.am.activityUrls?.length ? (
                  <span style={{ marginLeft: 8 }}>
                    <a href={day.am.activityUrls[0]} target="_blank" rel="noreferrer">
                      View activity
                    </a>
                  </span>
                ) : null}
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
        {(() => {
          // Determine if PM section should be shown
          // Show on non-arrival days, or on arrival day if arrival is ≤1 PM or there's a PM activity
          const arrivalMinutes = extractMinutesFromDateTime(arrivalTime);
          const hasPmActivity = day.pm?.scheduleId; // Only true if there's an actual scheduled activity
          const shouldShowPM = !isArrivalDay || (isArrivalDay && (arrivalMinutes === null || arrivalMinutes <= 780 || hasPmActivity));

          if (shouldShowPM) {
            return (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ fontWeight: 700, minWidth: 60, whiteSpace: "nowrap" }}>{day.pm?.startTime || "PM"}</div>
                <div>
                  {day.pm ? (
                    <>
                      <span className="activity-text">
                        {renderActivityText(formatActivity(day.pm.activity, day.level))}
                      </span>
                      {day.pm.activityUrls?.length ? (
                        <span style={{ marginLeft: 8 }}>
                          <a href={day.pm.activityUrls[0]} target="_blank" rel="noreferrer">
                            View activity
                          </a>
                        </span>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
}

export default function GuestView() {
  const { guestId } = useParams();
  const [guest, setGuest] = useState(null);
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [guestRes, itinRes] = await Promise.all([
          fetch(`/api/guests/${guestId}`),
          fetch(`/api/guests/${guestId}/itinerary`),
        ]);

        if (!guestRes.ok || !itinRes.ok) {
          throw new Error("Unable to load itinerary.");
        }

        const guestJson = await guestRes.json();
        const itinJson = await itinRes.json();
        if (!cancelled) {
          setGuest(guestJson);
          setItinerary(itinJson);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Unable to load itinerary.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (guestId) load();

    return () => {
      cancelled = true;
    };
  }, [guestId]);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <style>{`
        .guest-days-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .guest-day-card {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        @media (max-width: 640px) {
          .guest-days-grid {
            grid-template-columns: 1fr;
          }
        }
        @media print {
          .guest-days-grid {
            grid-template-columns: 1fr 1fr;
          }
          .guest-day-card a {
            display: none;
          }
        }
      `}</style>
      {loading && <div>Loading itinerary…</div>}
      {error && <div style={{ color: "crimson" }}>{error}</div>}

      {!loading && !error && guest && itinerary && (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{guest.name || "Guest"}</div>
            <div style={{ marginTop: 6, opacity: 0.8 }}>
              {itinerary.arrivalDate ? `Arrive ${itinerary.arrivalDate.split("T")[0]}` : ""}
              {itinerary.arrivalDate && itinerary.departureDate ? " • " : ""}
              {itinerary.departureDate ? `Depart ${itinerary.departureDate.split("T")[0]}` : ""}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
              View-only itinerary
            </div>
          </div>

          {itinerary.days?.length ? (
            <div className="guest-days-grid">
              {itinerary.days.map((day) => (
                <DayCard
                  key={day.date}
                  day={{ ...day, level: guest.level }}
                  arrivalTime={itinerary.arrivalDate}
                  isArrivalDay={day.date === itinerary.arrivalDate?.split("T")[0]}
                />
              ))}
            </div>
          ) : (
            <div style={{ opacity: 0.7 }}>
              No scheduled days available. Please check arrival and departure dates.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
